import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthLayout from '../../../components/Auth/AuthLayout';
import FormField from '../../../components/Auth/FormField';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate login success
      toast.success('Admin login successful!');
      navigate('/admin');
    } catch (error) {
      setErrors({ submit: 'Invalid credentials. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const adminIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
  );

  return (
    <AuthLayout
      title="Admin Login"
      subtitle="Access the JetScanner administration panel"
      icon={adminIcon}
      iconBgColor="bg-jet-red-100"
      iconColor="text-jet-red-600"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField
          label="Email Address"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your admin email"
          required
          error={errors.email}
        />

        <FormField
          label="Password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Enter your password"
          required
          error={errors.password}
          showPasswordToggle
          showPassword={showPassword}
          onTogglePassword={() => setShowPassword(!showPassword)}
        />

        {/* Remember Me Checkbox */}
        <div className="flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              className="w-4 h-4 text-jet-red-600 border-jet-gray-300 rounded focus:ring-jet-red-500 focus:ring-2 cursor-pointer"
            />
            <span className="ml-2 text-sm text-jet-gray-600">Remember me</span>
          </label>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-jet-red-50 border border-jet-red-200 rounded-md p-3">
            <p className="text-sm text-jet-red-600">{errors.submit}</p>
          </div>
        )}

        {/* Submit Button */}
        <LoadingButton
          type="submit"
          loading={loading}
          loadingText="Signing in..."
        >
          Sign In to Admin Panel
        </LoadingButton>

        {/* Additional Links */}
        <div className="text-center space-y-3">
          <button
            type="button"
            onClick={() => navigate('/admin/auth/forgot-password')}
            className="text-jet-red-600 hover:text-jet-red-700 text-sm transition-colors"
          >
            Forgot your password?
          </button>
          
          <div className="text-sm text-jet-gray-600">
            Need admin access?{' '}
            <button
              type="button"
              onClick={() => navigate('/admin/auth/register')}
              className="text-jet-red-600 hover:text-jet-red-700 font-medium"
            >
              Request Account
            </button>
          </div>
        </div>

        {/* Back to Main Site */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-jet-gray-600 hover:text-jet-gray-900 text-sm transition-colors"
          >
            ← Back to Main Site
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AdminLoginPage;